import type { CollectionChildren, Node } from "@react-types/shared";
import { clsx } from "clsx";
import { cloneElement, type CSSProperties, type ReactElement, type ReactNode, type RefObject, useRef } from "react";
import { type AriaMenuOptions, useMenu, useMenuItem, useMenuSection, useSeparator } from "react-aria";
import { type TreeState, useTreeState } from "react-stately";

import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { Label } from "../Label/Label.tsx";
import { LoadingSpinner } from "../ProgressSpinner/LoadingSpinner.tsx";

export interface MenuListBoxProps<T> extends AriaMenuOptions<T> {
  children: CollectionChildren<T>;
  listBoxRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  style?: CSSProperties;
  footNote?: ReactNode;
  isLoading?: boolean;
}

export const MenuListBox = <T extends object>(props: MenuListBoxProps<T>): ReactNode => {
  const ref = useRef<HTMLDivElement>(null);
  const { listBoxRef = ref } = props;
  const state = useTreeState(props);
  const { menuProps } = useMenu(props, state, ref);

  return (
    <div
      {...menuProps}
      className={clsx(styleMenu.list, styleMenu.menu, props.className)}
      ref={listBoxRef}
      style={props.style}
      role="listbox"
    >
      {props.isLoading ? <LoadingSpinner /> : null}
      {[...state.collection].map((item) =>
        item.type === "section" ? (
          <MenuSection key={item.key} section={item} state={state} />
        ) : (
          <MenuOption key={item.key} item={item} state={state} />
        ),
      )}
      {props.footNote}
    </div>
  );
};

interface MenuSectionProps<T> {
  section: Node<T>;
  state: TreeState<T>;
}

const MenuSection = <T extends object>({ section, state }: MenuSectionProps<T>): ReactNode => {
  const { itemProps, headingProps, groupProps } = useMenuSection({
    heading: section.rendered,
    "aria-label": section["aria-label"],
  });

  const { separatorProps } = useSeparator({ elementType: "div" });

  return (
    <>
      {section.key !== state.collection.getFirstKey() && (
        <div
          role="presentation"
          {...separatorProps}
          className={styleMenu.separator}
          style={{ margin: "3px 0", flex: "0 0 auto" }}
        />
      )}
      <div {...itemProps} style={{ flex: "0 0 auto" }} data-testid="combobox-menu-section">
        {section.rendered != undefined && (
          <Label {...headingProps} className={clsx(styleMenu.category_label, headingProps.className)}>
            {section.rendered}
          </Label>
        )}
        <div {...groupProps}>
          {[...(state.collection.getChildren?.(section.key) ?? [...section.childNodes])].map((node) => (
            <MenuOption key={node.key} item={node} state={state} />
          ))}
        </div>
      </div>
    </>
  );
};

/** Extends the Option with that interface */
export interface CollectionItem {
  isFocused?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
}

interface MenuOptionProps<T> {
  state: TreeState<T>;
  item: Node<T>;
}

const MenuOption = <T extends object>({ item, state }: MenuOptionProps<T>): ReactNode => {
  const ref = useRef<HTMLDivElement>(null);
  const { menuItemProps, isFocused, isDisabled, isSelected } = useMenuItem({ key: item.key }, state, ref);

  const dataAttributes = useDataAttributes(item.props);
  const testId = `${dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__` : ""}combobox-menu-optionitem`;

  return (
    <div
      {...menuItemProps}
      className={clsx(styleMenu.item_container, { [styleMenu.item_focus]: isFocused })}
      ref={ref}
      {...dataAttributes}
      data-testid={testId}
    >
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {cloneElement(item.rendered as ReactElement<any>, { isFocused, isDisabled, isSelected })}
    </div>
  );
};
